import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../components/icons';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatDateTime } from '../utils/format';

// TS port of the source (Falah) app's pages/AnnouncementDetail.jsx — ported
// as-is.
interface AnnouncementRecord {
  id: number;
  title: string;
  body: string;
  priority: string;
  published_at: string | null;
  expires_at: string | null;
  author: { full_name: string } | null;
}

export function AnnouncementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [announcement, setAnnouncement] = useState<AnnouncementRecord | null>(null);

  useEffect(() => {
    api.get<AnnouncementRecord>(`/announcements/${id}`).then((res) => setAnnouncement(res.data));
  }, [id]);

  const remove = async () => {
    if (!announcement) return;
    if (!(await confirmDelete(`Delete "${announcement.title}"?`))) return;
    try {
      await api.delete(`/announcements/${id}`);
      navigate('/announcements');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!announcement) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/announcements')} />
          <IconButton icon={IconEdit} label="Edit" to={`/announcements/${id}/edit`} />
          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{announcement.title}</div>
            <span className={`badge badge-${announcement.priority}`}>{announcement.priority}</span>
            <div className="detail-summary-service">By {announcement.author?.full_name ?? 'Unknown'}</div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Published</span>
            <span className="detail-item-value">{formatDateTime(announcement.published_at)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Expires</span>
            <span className={`detail-item-value${announcement.expires_at ? '' : ' empty'}`}>
              {formatDateTime(announcement.expires_at)}
            </span>
          </div>
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Body</span>
          <p className="detail-notes">{announcement.body}</p>
        </div>
      </div>
    </>
  );
}
